import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

export function plural(count: number, one: string, few: string, many: string): string {
  const mod10 = count % 10
  const mod100 = count % 100
  if (count === 1) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

/**
 * Copy text, and say honestly whether it worked.
 *
 * `navigator.clipboard` only exists in a secure context, so over plain HTTP on
 * a LAN address (which is how this runs before it reaches a domain) it is
 * simply undefined. The old code called it behind `?.`, swallowed the nothing
 * that happened, and showed a success toast for a copy that never occurred.
 * The fallback is the pre-Clipboard-API trick, and when even that fails the
 * caller is told so and can show the text to be copied by hand.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // Fall through: a rejected permission is not worse than no API at all.
  }
  try {
    const field = document.createElement('textarea')
    field.value = text
    field.setAttribute('readonly', '')
    field.style.position = 'fixed'
    field.style.opacity = '0'
    document.body.appendChild(field)
    field.select()
    const copied = document.execCommand('copy')
    document.body.removeChild(field)
    return copied
  } catch {
    return false
  }
}
